import { BrandLoader } from "@/components/brand/BrandLoader";
import { Logo } from "@/components/brand/Logo";
import { ChatWindow } from "@/components/chat/ChatWindow";
import { LanguageGate } from "@/components/chat/LanguageGate";
import { LanguageSwitcher } from "@/components/chat/LanguageSwitcher";
import { NameGate } from "@/components/chat/NameGate";
import { QuickReplyChips } from "@/components/chat/QuickReplyChips";
import { propertyChatService } from "@/lib/api";
import { clearStoredVisitorId, readStoredVisitorId, writeStoredVisitorId } from "@/lib/visitor";
import { useGuestLanguage } from "@/stores/language";
import type { ActionButton, ChatMessage } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { MapPinOff } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

export function PropertyChatPage() {
  const { token = "" } = useParams<{ token: string }>();
  const { lang, setLang } = useGuestLanguage(token);
  const queryClient = useQueryClient();
  const [visitorId, setVisitorId] = useState<string | null>(() => readStoredVisitorId(token));
  const [optimistic, setOptimistic] = useState<ChatMessage[]>([]);

  const sessionKey = ["property-session", token, lang, visitorId];
  const { data, isLoading, isError } = useQuery({
    queryKey: sessionKey,
    queryFn: () =>
      propertyChatService.session(token, {
        lang: lang ?? undefined,
        visitorId: visitorId ?? undefined,
      }),
    enabled: Boolean(token) && Boolean(lang),
    retry: false,
  });

  // Stale visitor id (server couldn't restore it) — reset and re-gate.
  if (data && visitorId && !data.visitor) {
    clearStoredVisitorId(token);
    setVisitorId(null);
  }

  const createVisitorMutation = useMutation({
    mutationFn: (name: string) =>
      propertyChatService.createVisitor(token, { name, lang: lang ?? "ko" }),
    onSuccess: (visitor) => {
      writeStoredVisitorId(token, visitor.visitor_id);
      setVisitorId(visitor.visitor_id);
    },
    onError: () => toast.error("시작하지 못했습니다. 다시 시도해주세요."),
  });

  const sendMutation = useMutation({
    mutationFn: (vars: { message: string; guideId?: string }) =>
      propertyChatService.send(token, {
        visitorId: visitorId ?? "",
        message: vars.message,
        lang: lang ?? undefined,
        guideId: vars.guideId,
      }),
    onSuccess: () => {
      setOptimistic([]);
      queryClient.invalidateQueries({ queryKey: ["property-session", token] });
    },
    onError: (error) => {
      setOptimistic((prev) => prev.filter((m) => m.role !== "user"));
      if (isAxiosError(error) && error.response?.status === 429) {
        toast.error("메시지가 너무 많습니다. 잠시 후 다시 시도해주세요.");
      } else {
        toast.error("메시지를 보내지 못했습니다. 다시 시도해주세요.");
      }
    },
  });

  const dispatch = (content: string, guideId?: string) => {
    setOptimistic((prev) => [
      ...prev,
      {
        id: `optimistic-${Date.now()}`,
        role: "user",
        content,
        created_at: new Date().toISOString(),
      },
    ]);
    sendMutation.mutate({ message: content, guideId });
  };

  if (!lang) {
    return <LanguageGate onSelect={setLang} />;
  }

  if (isLoading) {
    return (
      <div className="bg-atmosphere grain">
        <BrandLoader fullscreen label="컨시어지를 부르는 중…" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="bg-atmosphere grain flex h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <div className="relative z-10 flex flex-col items-center gap-4">
          <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
            <MapPinOff className="h-7 w-7" />
          </span>
          <div className="space-y-1.5">
            <h1 className="font-display text-2xl tracking-tight text-foreground">
              유효하지 않은 링크입니다
            </h1>
            <p className="max-w-xs text-sm text-muted-foreground">
              링크가 만료되었거나 잘못되었습니다. 숙소에 문의해주세요.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!data.visitor) {
    return (
      <NameGate
        propertyName={data.property_name}
        submitting={createVisitorMutation.isPending}
        onSubmit={(name) => createVisitorMutation.mutate(name)}
      />
    );
  }

  const greetingMessage: ChatMessage = {
    id: "greeting",
    role: "assistant",
    content: data.greeting ?? "",
    created_at: new Date(0).toISOString(),
  };
  const messages: ChatMessage[] = [
    ...(data.greeting ? [greetingMessage] : []),
    ...data.messages,
    ...optimistic,
  ];

  const lead = (
    <section className="animate-rise overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
      <div className="relative h-36 sm:h-40">
        {data.intro_image_urls?.length ? (
          <div className="flex h-full gap-2 overflow-x-auto">
            {data.intro_image_urls.map((u) => (
              <img
                key={u}
                src={u}
                alt=""
                className="h-full w-full flex-shrink-0 object-cover object-[50%_60%]"
              />
            ))}
          </div>
        ) : (
          <img
            src="/brand/welcome-scene.png"
            alt=""
            className="h-full w-full object-cover object-[50%_60%]"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-card via-card/55 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-4">
          <p className="eyebrow-brass">환영합니다 · Welcome</p>
          <h1 className="font-display text-2xl leading-tight tracking-tight text-foreground">
            {data.property_name}
          </h1>
        </div>
      </div>
      {data.intro ? (
        <p className="px-4 pb-3 pt-3 text-sm leading-relaxed text-muted-foreground">{data.intro}</p>
      ) : null}
    </section>
  );

  return (
    <div className="bg-atmosphere grain flex h-dvh flex-col">
      <header className="sticky top-0 z-20 border-b border-border/50 bg-card/70 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-2xl items-center justify-between gap-3 px-4 py-2.5">
          <div className="flex min-w-0 items-center gap-2.5">
            <Logo size={32} />
            <p className="truncate font-display text-base font-medium tracking-tight text-foreground">
              {data.property_name}
            </p>
          </div>
          <LanguageSwitcher value={lang} onChange={setLang} />
        </div>
      </header>
      <ChatWindow
        messages={messages}
        isTyping={sendMutation.isPending}
        disabled={sendMutation.isPending}
        onSend={(content) => dispatch(content)}
        lead={lead}
        belowMessages={
          <QuickReplyChips
            buttons={data.action_buttons}
            disabled={sendMutation.isPending}
            onPick={(b: ActionButton) => dispatch(b.label, b.guide_id)}
          />
        }
      />
    </div>
  );
}
